import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import {
    LayoutModule,
    SharedUiModule,
    TemplateDrawerComponent,
} from '@pf/shared-ui';
import { NZ_DRAWER_DATA, NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
    ContainerDto,
    EquipmentDto,
    SlotDto,
    SlotFacade,
    ZoneDto,
    ZoneFacade,
} from '@pf/platform-wms/domain';
import {
    BarcodeScanEvent,
    CpwScanInputComponent,
} from './cpw-scan-input.component';
import { ZoneType } from '@control-tower/platform-wms';
import { isHandlingLtl } from '@cpw/domain';
import { asArraySafe } from '@pf/shared-utility';

export interface CpwStagingDrawerData {
    zoneType: ZoneType;
    equipmentIds: string[];
    equipment: EquipmentDto[];
}

export interface StagingAssignment {
    zoneId?: string;
    slotId?: string;
    isLtl: boolean;
    equipmentId?: string; // For LTL containers, track which equipment they belong to
}

export type StagingAssignments = Record<string, StagingAssignment>;

interface AssignmentEntry {
    zoneId?: string;
    slotId?: string;
    isLtl: boolean;
    chips: { label: string }[];
    equipmentId?: string; // For LTL containers
}

interface EquipmentAssignment {
    equipment: EquipmentDto;
    displayName: string;
    isLtl: boolean;
    assigned: boolean;
    locationDisplay: { label: string }[];
}

interface ContainerAssignment {
    container: ContainerDto;
    equipmentId: string;
    displayName: string;
    assigned: boolean;
    locationDisplay: { label: string }[];
}

interface StagingGroup {
    isLtl: boolean;
    label: string;
    items: (EquipmentAssignment | ContainerAssignment)[];
}

@Component({
    selector: 'cpw-staging-drawer',
    imports: [CpwScanInputComponent, SharedUiModule, LayoutModule],
    templateUrl: './cpw-staging-drawer.component.html',
    styleUrl: './cpw-staging-drawer.component.scss',
    standalone: true,
})
export class CpwStagingDrawerComponent
    implements TemplateDrawerComponent<CpwStagingDrawerData>
{
    @ViewChild('scanInput') scanInput?: CpwScanInputComponent;

    protected readonly zoneFacade = inject(ZoneFacade);
    protected readonly slotFacade = inject(SlotFacade);
    readonly data: CpwStagingDrawerData = inject(NZ_DRAWER_DATA);
    drawerRef = inject(NzDrawerRef);

    isUnstaging = computed(() => this.data.zoneType === ZoneType.Normal);

    private readonly assignmentMap = signal<Record<string, AssignmentEntry>>(
        {}
    );

    groups = computed<StagingGroup[]>(() => {
        const map = this.assignmentMap();
        const ltlContainers: ContainerAssignment[] = [];
        const nonLtlItems: EquipmentAssignment[] = [];

        for (const eq of this.data.equipment) {
            const isLtl = isHandlingLtl(eq.equipmentType);

            if (isLtl) {
                // For LTL equipment, list each container by LPN
                const containers = asArraySafe(eq.containers);
                for (const container of containers) {
                    const assignment = map[container.id];
                    ltlContainers.push({
                        container,
                        equipmentId: eq.id,
                        displayName: container.lpn || 'Unknown LPN',
                        assigned: !!assignment,
                        locationDisplay: assignment?.chips ?? [],
                    });
                }
            } else {
                // For non-LTL equipment, list the equipment itself
                const assignment = map[eq.id];
                nonLtlItems.push({
                    equipment: eq,
                    displayName: `${eq.equipmentType?.name} ${eq.barcode}`,
                    isLtl: false,
                    assigned: !!assignment,
                    locationDisplay: assignment?.chips ?? [],
                });
            }
        }

        const groups: StagingGroup[] = [];
        if (ltlContainers.length) {
            groups.push({
                isLtl: true,
                label: 'LTL Containers',
                items: ltlContainers,
            });
        }
        if (nonLtlItems.length) {
            groups.push({
                isLtl: false,
                label: 'Parts Equipment',
                items: nonLtlItems,
            });
        }
        return groups;
    });

    nextUnassigned = computed(() => {
        for (const group of this.groups()) {
            const item = group.items.find(i => !i.assigned);
            if (item) {
                return { group, item };
            }
        }
        return null;
    });

    allAssigned = computed(() => !this.nextUnassigned());
    confirmInFlight = signal(false);

    activeScanFacade = computed(() => {
        const next = this.nextUnassigned();
        return next?.group.isLtl ? this.slotFacade : this.zoneFacade;
    });

    scanLabel = computed(() => {
        const next = this.nextUnassigned();
        if (!next) return '';

        const isMultiple = next.group.items.length > 1;
        const suffix = isMultiple ? ` for ${next.item.displayName}` : '';

        if (next.group.isLtl) {
            return `Scan slot${suffix}`;
        }
        return this.isUnstaging()
            ? `Scan or enter destination location${suffix}`
            : `Scan or enter staging location${suffix}`;
    });

    defaultScanError = computed(() => {
        const next = this.nextUnassigned();
        if (next?.group.isLtl) {
            return `The scanned slot is not in a ${this.data.zoneType} zone.`;
        }
        return `The scanned location is not designated for ${this.data.zoneType}.`;
    });

    firstGroupAssignment = computed(() => {
        const next = this.nextUnassigned();
        if (!next) return null;

        const firstAssigned = next.group.items.find(i => i.assigned);
        if (!firstAssigned) return null;

        const firstItemId = next.group.isLtl
            ? (firstAssigned as ContainerAssignment).container.id
            : (firstAssigned as EquipmentAssignment).equipment.id;
        const firstEntry = this.assignmentMap()[firstItemId];
        if (!firstEntry) return null;

        const remaining = next.group.items.filter(i => !i.assigned).length;
        if (remaining === 0) return null;

        return {
            entry: firstEntry,
            chips: firstAssigned.locationDisplay,
            remaining,
        };
    });

    onBarcodeScanned($event: BarcodeScanEvent) {
        const next = this.nextUnassigned();
        if (!next) return;

        if (next.group.isLtl) {
            const slot = $event.entity as SlotDto;
            if (slot.zone?.zoneType !== this.data.zoneType) {
                $event.processed.next(false);
                return;
            }
            const chips = slot.zone.locationIdentifiers?.map(l => ({
                label: l.name || '',
            })) || [{ label: slot.barcode || 'Slot' }];
            const containerItem = next.item as ContainerAssignment;
            this.assignItem(containerItem.container.id, {
                slotId: slot.id,
                isLtl: true,
                chips,
                equipmentId: containerItem.equipmentId,
            });
        } else {
            const zone = $event.entity as ZoneDto;
            if (zone.zoneType !== this.data.zoneType) {
                $event.processed.next(false);
                return;
            }
            const chips = zone.locationIdentifiers?.map(l => ({
                label: l.name,
            })) || [{ label: zone.barcode || '' }];
            const equipmentItem = next.item as EquipmentAssignment;
            this.assignItem(equipmentItem.equipment.id, {
                zoneId: zone.id,
                isLtl: false,
                chips,
            });
        }
        $event.processed.next(true);
        this.refocusScanInput();
    }

    applyToAll() {
        const first = this.firstGroupAssignment();
        const next = this.nextUnassigned();
        if (!first || !next) return;

        const updates: Record<string, AssignmentEntry> = {};
        for (const item of next.group.items) {
            if (!item.assigned) {
                const itemId = next.group.isLtl
                    ? (item as ContainerAssignment).container.id
                    : (item as EquipmentAssignment).equipment.id;
                updates[itemId] = { ...first.entry };
            }
        }
        this.assignmentMap.update(map => ({ ...map, ...updates }));
    }

    confirmStaging() {
        if (this.confirmInFlight()) return;
        this.confirmInFlight.set(true);
        const assignments: StagingAssignments = {};
        const map = this.assignmentMap();
        for (const [itemId, entry] of Object.entries(map)) {
            assignments[itemId] = {
                zoneId: entry.zoneId,
                slotId: entry.slotId,
                isLtl: entry.isLtl,
                equipmentId: entry.equipmentId,
            };
        }
        this.drawerRef.close(assignments);
    }

    private assignItem(itemId: string, entry: AssignmentEntry) {
        this.assignmentMap.update(map => ({
            ...map,
            [itemId]: entry,
        }));
    }

    trackByItem(
        index: number,
        item: EquipmentAssignment | ContainerAssignment
    ) {
        if ('container' in item) {
            return item.container.id;
        }
        return item.equipment.id;
    }

    private refocusScanInput() {
        setTimeout(() => {
            this.scanInput?.focus(true, true);
        }, 100);
    }
}
